import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router-dom'
import type {PartnerPatientSlotHistoryResponse} from '@generated/models'
import type {SlotStatusType} from '@common/models'
import {useEnumTranslations} from '@common/utils/translations'
import {EnumType} from '@common/utils/types'

import {HistoryList, HistoryItem} from './ReportsPatientPage.styled'
import {BlockSlotModal} from './BlockSlotModal'

import {Typography} from '~/components/general/typography/Typography'
import {Container} from '~/components/general/container/Container'
import {SideModal} from '~/components/general/modal/SideModal'
import {Button} from '~/components/general/button/Button'
import {useGetPartnerSlotReport} from '~/api/partnerAdminReports'
import {Loading} from '~/components/general/loading/Loading'

type SlotDetailsModalProps = {
  isVisible: boolean
  onClose: () => void
  slotId: string | null
  patientId?: string | null
  partnerServiceId?: string | null
  slotStatus?: SlotStatusType
  showViewPatientButton?: boolean
}

export const SlotDetailsModal = ({
  isVisible,
  onClose,
  slotId,
  patientId,
  partnerServiceId,
  slotStatus,
  showViewPatientButton = false,
}: SlotDetailsModalProps) => {
  const {t} = useTranslation()
  const {translateEnumValue} = useEnumTranslations()
  const navigate = useNavigate()
  const [showBlockModal, setShowBlockModal] = useState(false)

  const {data: history, isLoading} = useGetPartnerSlotReport(slotId || '', {
    enabled: !!slotId && isVisible,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  })

  const handleViewPatient = () => {
    if (patientId) {
      navigate(`/app/reports/patient?patientId=${patientId}`)
      onClose()
    }
  }

  const handleBlockClick = () => {
    setShowBlockModal(true)
  }

  const handleBlockModalClose = () => {
    setShowBlockModal(false)
    onClose()
  }

  const showBlockButton = slotStatus === 'AVAILABLE' && !!partnerServiceId
  const showUnblockButton = slotStatus === 'BLOCKED' && !!partnerServiceId

  return (
    <SideModal
      isVisible={isVisible}
      onClose={onClose}
      header={
        <Typography size="lg" weight="medium">
          {t('Slot Details')}
        </Typography>
      }>
      <Container direction="column" spacing={2}>
        <Container justify="flex-end" spacing={1}>
          {showBlockButton && (
            <Button onClick={handleBlockClick} color="primary" size="md">
              {t('Block Slot')}
            </Button>
          )}
          {showUnblockButton && (
            <Button onClick={handleBlockClick} color="primary" size="md">
              {t('Unblock Slot')}
            </Button>
          )}
          {showViewPatientButton && patientId && (
            <Button onClick={handleViewPatient} color="primary" size="md">
              {t('View Patient')}
            </Button>
          )}
        </Container>
        {isLoading && <Loading />}
        {!isLoading && history && history.length > 0 && (
          <HistoryList>
            {history.map(
              (h: PartnerPatientSlotHistoryResponse, idx: number) => (
                <HistoryItem key={`${slotId}-${idx}`}>
                  <Container justify="flex-end" align="center">
                    <Typography weight="bold">
                      {translateEnumValue(
                        EnumType.SlotStatusType,
                        h.status as SlotStatusType,
                        '-',
                      )}
                    </Typography>
                  </Container>
                  <Typography>
                    {t('Updated at')}: {h.at || '-'}
                  </Typography>
                  {h.comment && (
                    <Typography color="muted">
                      {t('Comment')}: {h.comment}
                    </Typography>
                  )}
                  {h.by && (
                    <Typography color="muted">
                      {t('By')}: {h.by}
                    </Typography>
                  )}
                  {h.patientName && (
                    <Typography color="muted">
                      {t('Patient')}: {h.patientName}
                    </Typography>
                  )}
                  {h.practitionerName && (
                    <Typography color="muted">
                      {t('Staff')}: {h.practitionerName}
                    </Typography>
                  )}
                </HistoryItem>
              ),
            )}
          </HistoryList>
        )}
        {!isLoading && (!history || history.length === 0) && (
          <Typography color="muted">{t('No history available')}</Typography>
        )}
      </Container>
      {showBlockModal && slotId && partnerServiceId && (
        <BlockSlotModal
          isVisible={showBlockModal}
          onClose={() => setShowBlockModal(false)}
          slotId={slotId}
          partnerServiceId={partnerServiceId}
          isBlock={slotStatus === 'AVAILABLE'}
          onSuccess={handleBlockModalClose}
        />
      )}
    </SideModal>
  )
}
